import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';

const KIND_LABELS = {
  buy: 'Buy', sell: 'Sell', deposit: 'Deposit', withdrawal: 'Withdrawal',
  dividend: 'Dividend', interest: 'Interest', fee: 'Fee',
};
const STATUS_LABELS = {
  to_load: 'To load', loaded: 'Loaded', duplicate: 'Duplicate', invalid: 'Invalid', skipped: 'Skipped',
};
const DATE_FORMATS = [
  { value: 'iso', label: 'YYYY-MM-DD' },
  { value: 'mdy', label: 'MM/DD/YYYY' },
  { value: 'dmy', label: 'DD/MM/YYYY' },
];
const FILTERS = [
  { value: 'all', label: 'All rows' },
  { value: 'to_load', label: 'To load' },
  { value: 'sells', label: 'Sells' },
  { value: 'attention', label: 'Needs a look' },
];

const fmt = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const qty = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 6 }));

// Which rows a filter shows. "Needs a look" is anything not loading as-is: rows
// left out, and sells whose lots aren't confirmed yet.
const matches = (r, filter) => {
  if (filter === 'to_load') return r.status === 'to_load';
  if (filter === 'sells') return r.kind === 'sell';
  if (filter === 'attention') {
    return ['invalid', 'skipped', 'duplicate'].includes(r.status)
      || (r.kind === 'sell' && r.status === 'to_load' && (!r.lots_confirmed || r.lot_problem));
  }
  return true;
};

// A sell's lots as text: "2024-03-01 × 5 @ $85.00; file row 12 × 2 @ $90.00".
function LotSummary({ lots, labels }) {
  if (!lots || lots.length === 0) return null;
  return (
    <span>
      {lots.map((a, i) => {
        const l = labels[a.lot] || {};
        const where = l.row_number ? `row ${l.row_number} (${l.date})` : l.date || a.lot;
        return <span key={a.lot}>{i > 0 && '; '}{where} × {qty(a.quantity)}{l.cost_per_share != null && ` @ ${fmt(l.cost_per_share)}`}</span>;
      })}
    </span>
  );
}

// Choosing the lots for one staged sell: the lots available to it (stored buys
// and buys in this file), with how many shares to take from each.
function LotEditor({ batch, row, onSaved, onCancel }) {
  const [choices, setChoices] = useState(null);
  const [picked, setPicked] = useState(() => Object.fromEntries((row.lots || []).map(a => [a.lot, a.quantity])));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getImportLotChoices(batch.id, row.id).then(setChoices).catch(e => setError(e.message));
  }, [batch.id, row.id]);

  const total = Object.values(picked).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const roundedTotal = Math.round(total * 1e6) / 1e6;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const allocations = Object.entries(picked)
        .map(([lot, v]) => ({ lot, quantity: parseFloat(v) || 0 }))
        .filter(a => a.quantity > 0);
      onSaved(await api.setImportLots(batch.id, row.id, allocations));
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  return (
    <div style={{ padding: '8px 0' }}>
      {choices === null ? <div className="muted" style={{ fontSize: 13 }}>{error || 'Loading lots...'}</div> : (
        <>
          <table>
            <thead>
              <tr><th>Lot</th><th style={{ textAlign: 'right' }}>Available</th><th style={{ textAlign: 'right' }}>Cost / Share</th><th style={{ textAlign: 'right' }}>Sell</th></tr>
            </thead>
            <tbody>
              {choices.map(l => (
                <tr key={l.lot}>
                  <td className="mono">{l.date}{l.row_number ? <span className="muted"> · file row {l.row_number}</span> : <span className="muted"> · recorded</span>}</td>
                  <td className="mono amount">
                    <span style={{ cursor: 'pointer', color: 'var(--accent)' }} title="Sell the whole lot"
                      onClick={() => setPicked(p => ({ ...p, [l.lot]: Math.min(l.available, Math.max(row.quantity - (total - (parseFloat(p[l.lot]) || 0)), 0)) }))}>
                      {qty(l.available)}
                    </span>
                  </td>
                  <td className="mono amount">{fmt(l.cost_per_share)}</td>
                  <td className="amount">
                    <input type="number" min="0" step="any" style={{ width: 110, textAlign: 'right' }}
                      value={picked[l.lot] ?? ''} onChange={e => setPicked(p => ({ ...p, [l.lot]: e.target.value }))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 13, margin: '8px 0' }} className={Math.abs(roundedTotal - row.quantity) > 1e-6 ? 'loss' : 'muted'}>
            {qty(roundedTotal)} of {qty(row.quantity)} shares selected
          </div>
          {error && <div className="loss" style={{ fontSize: 13, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={save} disabled={busy || Math.abs(roundedTotal - row.quantity) > 1e-6}>
              {busy ? 'Saving...' : 'Use These Lots'}
            </button>
            <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

// A staged or finished transaction import: counts, every row with its lots, and
// while it is staged the lot choices, Confirm, Load and Discard. Used both by the
// import card right after a file is checked and by the Import History detail,
// so an import can be picked up and finished later. `onBatch` receives the
// updated batch after each action; `onReset` (the card only) offers another file;
// `showHeader` is off where the account and file are already shown (the detail).
export function TransactionBatchView({ batch, onBatch, onChanged, onDiscarded, onReset, showHeader = true }) {
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const changed = () => { if (onChanged) onChanged(); };

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      const result = await fn();
      if (result) onBatch(result);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
    changed();
  };
  const confirmAll = () => run(() => api.confirmImportLots(batch.id));
  const load = () => run(() => api.loadImport(batch.id));
  const discard = async () => {
    setBusy(true);
    setError('');
    try {
      await api.discardImport(batch.id);
      if (onDiscarded) onDiscarded();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
    changed();
  };

  const staged = batch.status === 'staged';
  const c = batch.counts;
  const all = batch.rows || [];
  const rows = all.filter(r => matches(r, filter));
  const canLoad = staged && c.to_load > 0 && c.sells_unconfirmed === 0 && c.lot_problems === 0;

  return (
    <>
      {showHeader && (
        <div style={{ fontSize: 13, marginBottom: 12 }}>
          <strong>{batch.account_name}</strong> · <span className="muted">{batch.filename}</span>
          <div className="muted" style={{ marginTop: 4 }}>
            {batch.row_count} rows{batch.first_date ? `, ${batch.first_date} to ${batch.last_date}` : ''}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
        {[
          [staged ? 'To load' : 'Loaded', staged ? c.to_load : c.loaded, 'gain'],
          ['Duplicates', c.duplicate, ''],
          ['Invalid', c.invalid, c.invalid ? 'loss' : ''],
          ['Skipped', c.skipped, ''],
        ].map(([label, n, cls]) => (
          <div key={label} className="stat-card" style={{ textAlign: 'right' }}>
            <div className="stat-label">{label}</div>
            <div className={`stat-value mono ${cls}`} style={{ fontSize: 16 }}>{n}</div>
          </div>
        ))}
      </div>

      {batch.error && <div className="loss" style={{ fontSize: 13, marginBottom: 8 }}>{batch.error}</div>}
      {(batch.warnings || []).map((w, i) => (
        <div key={i} className="muted" style={{ fontSize: 13, marginBottom: 8 }}>{w}</div>
      ))}
      {c.duplicate > 0 && (
        <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
          Duplicates match a trade or cash entry already recorded in this account; they are never loaded twice.
        </div>
      )}
      {staged && c.lot_problems > 0 && (
        <div className="loss" style={{ fontSize: 13, marginBottom: 8 }}>
          {c.lot_problems} sells sell more shares than the account holds. Record the missing buys, then discard this import and check the file again.
        </div>
      )}
      {staged && c.sells_unconfirmed > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, marginBottom: 12 }}>
          <span>{c.sells_unconfirmed} sells have lots chosen oldest-first. Check them below, change any that sold different lots, then confirm.</span>
          <button className="btn" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }} onClick={confirmAll} disabled={busy}>
            Confirm {c.sells_unconfirmed} Sells
          </button>
        </div>
      )}

      {all.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <select style={{ width: 'auto' }} value={filter} onChange={e => setFilter(e.target.value)}>
              {FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <span className="muted" style={{ fontSize: 13, marginLeft: 'auto' }}>
              {rows.length} of {batch.rows_total} rows{batch.rows_total > all.length ? ` (first ${all.length} shown)` : ''}
            </span>
          </div>

          <div style={{ maxHeight: 420, overflowY: 'auto', marginBottom: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Row</th><th>Date</th><th>Type</th><th>Symbol</th>
                  <th style={{ textAlign: 'right' }}>Quantity</th><th style={{ textAlign: 'right' }}>Price</th>
                  <th style={{ textAlign: 'right' }}>Amount</th><th>Status</th><th>Lots / Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const sellToLoad = staged && r.kind === 'sell' && r.status === 'to_load';
                  return (
                    <React.Fragment key={r.id}>
                      <tr>
                        <td className="mono muted">{r.row_number}</td>
                        <td className="mono" style={{ whiteSpace: 'nowrap' }}>{r.date || (r.raw && r.raw.date) || '—'}</td>
                        <td>{KIND_LABELS[r.kind] || <span className="muted">{(r.raw && r.raw.type) || '—'}</span>}</td>
                        <td className="mono" style={{ color: r.symbol ? 'var(--accent)' : undefined }}>{r.symbol || ''}</td>
                        <td className="mono amount">{r.quantity != null ? qty(r.quantity) : ''}</td>
                        <td className="mono amount">{r.price != null ? fmt(r.price) : ''}</td>
                        <td className="mono amount">{r.amount != null ? fmt(r.amount) : ''}</td>
                        <td className={r.status === 'invalid' ? 'loss' : r.status === 'to_load' || r.status === 'loaded' ? '' : 'muted'} style={{ whiteSpace: 'nowrap' }}>
                          {STATUS_LABELS[r.status] || r.status}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {r.kind === 'sell' && r.lot_problem && <span className="loss">{r.lot_problem}</span>}
                          {r.kind === 'sell' && !r.lot_problem && r.lots && r.lots.length > 0 && (
                            <>
                              <LotSummary lots={r.lots} labels={batch.lot_labels || {}} />
                              {sellToLoad && (
                                <>
                                  {' '}
                                  <span className={r.lots_confirmed ? 'gain' : 'muted'}>{r.lots_confirmed ? '· Confirmed' : '· Oldest first, not confirmed'}</span>
                                  {' '}
                                  <span style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => setEditing(editing === r.id ? null : r.id)}>
                                    {editing === r.id ? 'Close' : 'Change'}
                                  </span>
                                </>
                              )}
                            </>
                          )}
                          {r.notes && <span className="muted">{r.notes}</span>}
                        </td>
                      </tr>
                      {editing === r.id && (
                        <tr>
                          <td colSpan={9}>
                            <LotEditor batch={batch} row={r}
                              onSaved={(b) => { onBatch(b); setEditing(null); changed(); }}
                              onCancel={() => setEditing(null)} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="muted" style={{ textAlign: 'center', padding: 20 }}>No rows match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {error && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {staged && (
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={load} disabled={busy || !canLoad}
            title={c.sells_unconfirmed > 0 ? 'Confirm the lots for every sell first' : undefined}>
            {busy ? 'Working...' : c.to_load === 0 ? 'Nothing new to load' : `Load ${c.to_load} rows`}
          </button>
          <button className="btn-ghost" onClick={discard} disabled={busy}>Discard</button>
        </div>
      )}
      {batch.status === 'loaded' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13 }}>
            <span className="gain">Loaded {batch.loaded_count} rows into {batch.account_name}.</span>
            {c.invalid > 0 && <span className="muted"> {c.invalid} invalid rows were left out; the file is filed under processed-with-errors.</span>}
          </span>
          {onReset && <button className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={onReset}>Import Another File</button>}
        </div>
      )}
    </>
  );
}

// Imports trades and cash entries for one account from a brokerage activity
// export. A brokerage preset reads its own columns; any other CSV is mapped by
// hand. The file is staged and checked first, every sell gets oldest-first lots
// to confirm or change, and nothing reaches the account until the user loads
// it, all rows at once. `onChanged` fires whenever an import is checked,
// loaded, discarded or rejected, so the page can refresh its list of imports.
export default function TransactionImport({ onChanged }) {
  const fileInput = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [file, setFile] = useState(null);
  const [content, setContent] = useState('');
  const [info, setInfo] = useState(null);
  const [format, setFormat] = useState('');
  const [mapping, setMapping] = useState({});
  const [dateFormat, setDateFormat] = useState('');
  const [fileAccount, setFileAccount] = useState('');
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    api.getAccounts().then(rows => setAccounts(rows || [])).catch(() => {});
  }, []);

  const changed = () => { if (onChanged) onChanged(); };

  const reset = () => {
    setFile(null);
    setContent('');
    setInfo(null);
    setBatch(null);
    setError('');
    if (fileInput.current) fileInput.current.value = '';
  };

  const applyInfo = (i) => {
    setInfo(i);
    setFormat(i.detected_format);
    setMapping(i.mapping || {});
    setDateFormat(i.date_format || '');
    setFileAccount(i.file_accounts.length === 1 ? i.file_accounts[0] : '');
  };

  const chooseFile = async (f) => {
    setBatch(null);
    setInfo(null);
    setError('');
    if (f && !/\.csv$/i.test(f.name)) {
      setFile(null);
      setError(`"${f.name}" is not a .csv file.`);
      return;
    }
    setFile(f || null);
    if (!f) return;
    setBusy(true);
    try {
      const text = await f.text();
      setContent(text);
      applyInfo(await api.inspectTransactionFile({ filename: f.name, content: text }));
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  // A different format reads different columns: ask the API what it would map.
  const chooseFormat = async (value) => {
    setFormat(value);
    setError('');
    try {
      const i = await api.inspectTransactionFile({ filename: file.name, content, broker_format: value });
      applyInfo({ ...i, detected_format: value });
    } catch (e) {
      setError(e.message);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    if (files.length > 1) {
      setFile(null);
      setError('Drop one file at a time.');
      return;
    }
    chooseFile(files[0]);
  };

  const selected = info ? info.formats.find(f => f.value === format) : null;
  const stage = async () => {
    setBusy(true);
    setError('');
    try {
      setBatch(await api.stageTransactionImport({
        filename: file.name,
        content,
        account_id: Number(accountId),
        broker_format: format,
        mapping: selected && selected.mapped_by_user ? mapping : null,
        date_format: dateFormat || null,
        file_account: fileAccount || null,
      }));
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
    changed();
  };

  const multiAccount = info && info.file_accounts.length > 1;
  const canStage = file && info && accountId && (!multiAccount || fileAccount)
    && (!selected || !selected.mapped_by_user || (mapping.date && mapping.type))
    && (!selected || !selected.mapped_by_user || dateFormat || info.date_format);

  return (
    <div className="card">
      <div className="modal-title">Import Transactions</div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Load trades and cash entries (deposits, dividends, interest, fees) for one account from a brokerage activity export. The file is checked first, and you confirm the lots for every sell. Nothing is saved until you load it, and then everything loads at once or not at all.
      </div>

      {!batch && (
        <>
          <div className="form-group">
            <label>File</label>
            <div
              role="button"
              tabIndex={0}
              aria-label="Choose a transaction file, or drag and drop one here"
              onClick={() => fileInput.current && fileInput.current.click()}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current && fileInput.current.click(); } }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragEnter={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              style={{
                border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 8,
                padding: '22px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {file
                ? <><strong>{file.name}</strong><div className="muted" style={{ marginTop: 4 }}>Drop a different file here, or click to choose another</div></>
                : <>Drag and drop a CSV file here<div className="muted" style={{ marginTop: 4 }}>or click to browse for one</div></>}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={e => { chooseFile(e.target.files[0]); e.target.value = ''; }}
            />
          </div>

          {info && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Load into account</label>
                  <select value={accountId} onChange={e => setAccountId(e.target.value)}>
                    <option value="">Choose an account</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>File format</label>
                  <select value={format} onChange={e => chooseFormat(e.target.value)}>
                    {info.formats.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              </div>

              {multiAccount && (
                <div className="form-group">
                  <label>Account in the file</label>
                  <select value={fileAccount} onChange={e => setFileAccount(e.target.value)}>
                    <option value="">This file has several accounts: choose one</option>
                    {info.file_accounts.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              )}

              {selected && selected.mapped_by_user && (
                <>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                    Match the file's columns. Date and Type are required, plus Amount or both Quantity and Price. The type of each row is read from words in its Type column (buy, sell, dividend, interest, deposit, withdrawal, fee).
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8, marginBottom: 8 }}>
                    {info.fields.map(f => (
                      <div key={f.field} className="form-group" style={{ marginBottom: 4 }}>
                        <label>{f.label}{f.required ? ' *' : ''}</label>
                        <select value={mapping[f.field] || ''} onChange={e => setMapping(m => ({ ...m, [f.field]: e.target.value || null }))}>
                          <option value="">—</option>
                          {info.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  {!info.date_format && (
                    <div className="form-group">
                      <label>Date format (it couldn't be worked out from the file)</label>
                      <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}>
                        <option value="">Choose a format</option>
                        {DATE_FORMATS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {error && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
          {info && (
            <button className="btn" onClick={stage} disabled={busy || !canStage}>
              {busy ? 'Checking...' : 'Check File'}
            </button>
          )}
        </>
      )}

      {batch && (
        <TransactionBatchView batch={batch} onBatch={setBatch} onChanged={changed}
          onDiscarded={reset} onReset={reset} />
      )}
    </div>
  );
}
